import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import {
  ArrowRight, ShieldCheck, Server, Network, Cloud, Video, Laptop, Wifi
} from 'lucide-react';

export default function Hero() {

  const stats = [
    { count: 1129, suffix: '+', label: 'Happy Clients' },
    { count: 428, suffix: '+', label: 'Projects Delivered' },
    { count: 38, suffix: '+', label: 'Skilled Experts' },
    { count: 25, suffix: '+', label: 'Years Experience' },
  ];

  return (
    <section id="home" className="relative min-h-screen pt-32 pb-24 flex items-center justify-center overflow-hidden bg-transparent">

      {/* Radial ambient glow backdrops */}
      <div className="absolute top-[20%] left-[-10%] w-[45vw] h-[45vw] rounded-full bg-brand-cyan/3 blur-[150px] pointer-events-none z-0" />
      <div className="absolute bottom-[20%] right-[-10%] w-[40vw] h-[40vw] rounded-full bg-brand-blue/3 blur-[150px] pointer-events-none z-0" />

      <div className="max-w-7xl mx-auto px-6 md:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-16 items-center relative z-10">

        {/* Left Column: Core Value Pitch */}
        <div className="lg:col-span-6 flex flex-col items-start text-left">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-cyan/10 border border-brand-cyan/20 text-brand-cyan text-xs font-bold tracking-wider uppercase mb-6 font-display"
          >
            <ShieldCheck size={14} className="text-brand-cyan" />
            <span>Trusted Enterprise ICT Partner Since 1999</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-display font-black text-5xl md:text-6xl xl:text-7xl text-white tracking-tight leading-[1.08] mb-6"
          >
            Next-Gen IT Infrastructure For <span className="text-gradient">Enterprises</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="font-sans text-base md:text-lg text-slate-300 leading-relaxed max-w-xl mb-8"
          >
            Deploy secure, scalable, and ultra-reliable networking systems, cloud computing nodes, and security solutions. Experience zero compromise systems integration.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mb-16"
          >
            <Link
              to="/services"
              className="flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-brand-cyan to-brand-blue hover:from-brand-blue hover:to-brand-cyan text-white font-bold text-sm tracking-wider shadow-lg shadow-brand-cyan/15 hover:shadow-brand-cyan/25 hover:translate-y-[-2px] transition-all duration-300"
            >
              <span>EXPLORE SOLUTIONS</span>
              <ArrowRight size={16} />
            </Link>

            <Link
              to="/contact"
              className="flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-white/5 hover:bg-white/10 text-white font-bold text-sm tracking-wider border border-white/10 hover:border-white/20 transition-all duration-300"
            >
              <span>CONTACT EXPERTS</span>
            </Link>
          </motion.div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 border-t border-white/5 pt-10 w-full">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.35 + idx * 0.08 }}
                className="flex flex-col"
              >
                <span className="font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight flex items-baseline">
                  <CountUp end={stat.count} duration={2.5} enableScrollSpy scrollSpyOnce />
                  <span className="text-brand-cyan ml-0.5">{stat.suffix}</span>
                </span>
                <span className="font-sans text-[10px] md:text-xs text-slate-400 font-semibold mt-1.5 uppercase tracking-widest leading-none">
                  {stat.label}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right Column: Services Bento Grid Hub */}
        <div className="lg:col-span-6 w-full relative flex items-center justify-center min-h-[440px]">
          {/* Subtle bento box glowing frame */}
          <div className="absolute inset-[-6%] rounded-full bg-brand-cyan/5 blur-3xl animate-pulse-slow pointer-events-none z-0" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-[560px] relative z-10">

            {/* 1. Server Solutions */}
            <motion.div
              whileHover={{ scale: 1.03 }}
              className="group relative rounded-2xl border border-white/5 bg-navy-dark/45 backdrop-blur-md p-5 shadow-xl hover:border-brand-cyan/20 hover:shadow-brand-cyan/5 transition-all duration-300 flex flex-col justify-between min-h-[140px]"
            >
              <div className="flex flex-col items-start text-left">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 rounded-lg bg-cyan-500/10 text-brand-cyan group-hover:bg-brand-cyan group-hover:text-navy-dark transition-colors duration-300">
                    <Server size={16} />
                  </div>
                  <span className="font-display font-bold text-sm text-white">Server Solutions</span>
                </div>
                <p className="text-[10px] text-slate-400 leading-relaxed mb-4">
                  Deploy VMware ESXi/Hyper-V virtualization, Active Directory domains, and local storage arrays.
                </p>
              </div>
              <div className="flex gap-1.5 mt-auto">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse delay-100"></span>
              </div>
            </motion.div>

            {/* 2. Enterprise Networking */}
            <motion.div
              whileHover={{ scale: 1.03 }}
              className="group relative rounded-2xl border border-white/5 bg-navy-dark/45 backdrop-blur-md p-5 shadow-xl hover:border-brand-cyan/20 hover:shadow-brand-cyan/5 transition-all duration-300 flex flex-col justify-between min-h-[140px]"
            >
              <div className="flex flex-col items-start text-left">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 rounded-lg bg-cyan-500/10 text-brand-cyan group-hover:bg-brand-cyan group-hover:text-navy-dark transition-colors duration-300">
                    <Network size={16} />
                  </div>
                  <span className="font-display font-bold text-sm text-white">Enterprise Networking</span>
                </div>
                <p className="text-[10px] text-slate-400 leading-relaxed mb-4">
                  Structured Cat6/fiber network cabling, core routing setups, managed switches, and VLAN layouts.
                </p>
              </div>
              <div className="flex gap-1.5 items-center mt-auto font-mono text-[8px] text-slate-500">
                <Wifi size={10} className="text-brand-cyan animate-pulse" />
                <span>1.2 Gbps LINK ACTIVE</span>
              </div>
            </motion.div>

            {/* 3. Cloud Services */}
            <motion.div
              whileHover={{ scale: 1.03 }}
              className="group relative rounded-2xl border border-white/5 bg-navy-dark/45 backdrop-blur-md p-5 shadow-xl hover:border-brand-cyan/20 hover:shadow-brand-cyan/5 transition-all duration-300 flex flex-col justify-between min-h-[140px]"
            >
              <div className="flex flex-col items-start text-left">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 rounded-lg bg-cyan-500/10 text-brand-cyan group-hover:bg-brand-cyan group-hover:text-navy-dark transition-colors duration-300">
                    <Cloud size={16} />
                  </div>
                  <span className="font-display font-bold text-sm text-white">Cloud Migrations</span>
                </div>
                <p className="text-[10px] text-slate-400 leading-relaxed mb-4">
                  Seamless workload transfers & automated off-site backups.
                </p>
              </div>
              <div className="w-full bg-slate-900/60 h-1 rounded overflow-hidden mt-auto">
                <div className="h-full bg-brand-cyan animate-pulse" style={{ width: '75%' }}></div>
              </div>
            </motion.div>

            {/* 4. CCTV Surveillance */}
            <motion.div
              whileHover={{ scale: 1.03 }}
              className="group relative rounded-2xl border border-white/5 bg-navy-dark/45 backdrop-blur-md p-5 shadow-xl hover:border-brand-cyan/20 hover:shadow-brand-cyan/5 transition-all duration-300 flex flex-col justify-between min-h-[140px]"
            >
              <div className="flex flex-col items-start text-left">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 rounded-lg bg-cyan-500/10 text-brand-cyan group-hover:bg-brand-cyan group-hover:text-navy-dark transition-colors duration-300">
                    <Video size={16} />
                  </div>
                  <span className="font-display font-bold text-sm text-white">CCTV Surveillance</span>
                </div>
                <p className="text-[10px] text-slate-400 leading-relaxed mb-4">
                  Ultra-HD 4K IP security cameras, NVR central recording installations, and AI motion notifications.
                </p>
              </div>
              <div className="flex items-center gap-1.5 mt-auto font-mono text-[8px] text-slate-500 font-bold uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-ping"></span>
                <span>REC // ONLINE</span>
              </div>
            </motion.div>

            {/* 5. Laptop & Desktop Rentals */}
            <motion.div
              whileHover={{ scale: 1.03 }}
              className="group relative rounded-2xl border border-white/5 bg-navy-dark/45 backdrop-blur-md p-5 shadow-xl hover:border-brand-cyan/20 hover:shadow-brand-cyan/5 transition-all duration-300 flex flex-col justify-between min-h-[140px]"
            >
              <div className="flex flex-col items-start text-left">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 rounded-lg bg-cyan-500/10 text-brand-cyan group-hover:bg-brand-cyan group-hover:text-navy-dark transition-colors duration-300">
                    <Laptop size={16} />
                  </div>
                  <span className="font-display font-bold text-sm text-white">Hardware Rentals</span>
                </div>
                <p className="text-[10px] text-slate-400 leading-relaxed mb-4">
                  Bulk leasing profiles of high-configuration laptops, thin clients, and workspaces for teams.
                </p>
              </div>
              <div className="flex items-center gap-1 mt-auto text-[8px] text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded uppercase font-mono tracking-wider">
                24/7 SWAP MAINTENANCE
              </div>
            </motion.div>

            {/* 6. Security */}
            <motion.div
              whileHover={{ scale: 1.03 }}
              className="group relative rounded-2xl border border-white/5 bg-navy-dark/45 backdrop-blur-md p-5 shadow-xl hover:border-brand-cyan/20 hover:shadow-brand-cyan/5 transition-all duration-300 flex flex-col justify-between min-h-[140px]"
            >
              <div className="flex flex-col items-start text-left">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 rounded-lg bg-cyan-500/10 text-brand-cyan group-hover:bg-brand-cyan group-hover:text-navy-dark transition-colors duration-300">
                    <ShieldCheck size={16} className="text-brand-cyan" />
                  </div>
                  <span className="font-display font-bold text-sm text-white">Security Solutions</span>
                </div>
                <p className="text-[10px] text-slate-400 leading-relaxed mb-4">
                  Next-generation firewall setups, endpoint detection agents, and vulnerability audits.
                </p>
              </div>
              <div className="flex gap-1 items-center mt-auto font-mono text-[8px] text-brand-cyan font-bold tracking-wide">
                <span>UTM THREAT SHIELD ACTIVED</span>
              </div>
            </motion.div>

          </div>
        </div>

      </div>
    </section>
  );
}
